/**
 * ScnSpecListPage (S7.2) — the SCN 共同規格主檔 list, the ONE net-new surface
 * of the product/pricing rebuild (交付稿 board A2).
 *
 * House list-kit stack: `useListPage` state triad + `GenericFilterPopover` +
 * `TableContainer` / `ListEmptyState`. Data is the READ-ONLY `ScnSpecGroup`
 * read model served by `GET /scn-groups` (S7.1) — spec metadata seeded once,
 * membership always derived from the products collection, so there are no
 * write affordances here (no create CTA, no row actions): groups are authored
 * by linking products (ScnLinkDrawer) and spec-metadata authoring is out of
 * scope until a board cites it.
 *
 * Columns per plan §S7.2: SCN code (`PurpleTag`, spec name secondary),
 * member count (board A2 關聯商品數; the 0-SKU row carries the 未啟用 badge —
 * count + text, never colour-only), OEM numbers, markets, updated.
 *
 * Local adaptations (module-checklist touchpoints owned by other files are
 * recorded as follow-ups, not silently skipped):
 * - endpoint constant is file-local until `api.constants.ts` (outside the
 *   slice file set) registers `/scn-groups`;
 * - fetch is a thin in-file table hook on `apiClient` (the VendorLinesSection
 *   precedent) until a `scn.api.ts` service exists;
 * - filter state is component-local `useState` — single-consumer surface, no
 *   dedicated Zustand store file in the slice set. Search + status only
 *   (board A2:「搜尋 SCN 代碼或規格名稱...」+ the derived 狀態 axis).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Input, Table, Tag, Tooltip, theme } from 'antd';
import type { TableProps } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAntdTable } from 'ahooks';
import PurpleTag from '@/components/common/PurpleTag';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import { useListPage } from '@/hooks/useListPage';
import type { TableHookResult } from '@/hooks/useListPage';
import { apiClient } from '@/services/api';
import type { ScnGroupListResponse, ScnGroupStatus, ScnSpecGroup } from '@/types/api/scn';
import { formatDate } from '@/utils/dateUtils';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';

/** Follow-up: register in `api.constants.ts` (file outside the S7.2 slice set). */
const SCN_GROUPS_ENDPOINT = '/scn-groups';

/** List filters — search (server-side) + derived status (client-side). */
interface ScnListFilters {
  search: string;
  status: ScnGroupStatus | 'all';
}

/** Popover-local filter value shape. */
interface ScnFilterPopoverValues extends FilterValues {
  status?: string;
}

/**
 * In-file table hook on the list-kit contract (`loading || params.length === 0`
 * pre-first-fetch guard). Search runs server-side (`matchesScnSearch` in the
 * handler); the handler's default `pageSize <= 0` returns every row, so the
 * derived-status filter + pagination stay client-side — the mock-scale
 * client-pagination precedent of `useProductsTable`'s union fetch.
 */
function useScnGroupsTable(filters: ScnListFilters): TableHookResult<ScnSpecGroup> {
  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const { data } = await apiClient.get<ScnGroupListResponse>(SCN_GROUPS_ENDPOINT, {
        params: { search: filters.search.trim() || undefined },
      });
      const rows =
        filters.status === 'all'
          ? data.data
          : data.data.filter((group) => group.status === filters.status);
      const start = (current - 1) * pageSize;
      return { total: rows.length, list: rows.slice(start, start + pageSize) };
    },
    { defaultPageSize: 10, refreshDeps: [filters.search, filters.status] }
  );

  return {
    tableProps,
    refresh,
    // Pre-first-fetch guard (house rule) — never flash the empty state.
    isLoading: loading || params.length === 0,
    error,
  };
}

const ScnSpecListPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['scn', 'common']);
  const { token } = theme.useToken();
  const isZh = i18n.language.startsWith('zh');

  const [filters, setFilters] = useState<ScnListFilters>({ search: '', status: 'all' });
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const resetFilters = () => setFilters({ search: '', status: 'all' });

  // Badge parity: status counts only when it deviates from the All default.
  const customFilterCounters = useMemo(
    () => ({ status: (value: unknown) => (value === 'all' ? 0 : 1) }),
    []
  );

  const { tableProps, isLoading, refresh, activeFilterCount, isFiltered, listState } = useListPage<
    ScnListFilters,
    ScnSpecGroup
  >({
    useTableHook: useScnGroupsTable,
    filters,
    filterCountExclusions: ['search'],
    customFilterCounters,
  });

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('scn:list.filters.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'in_use', label: t('scn:list.filters.statusInUse') },
          { value: 'inactive', label: t('scn:list.filters.statusInactive') },
          { value: 'all', label: t('scn:list.filters.statusAll') },
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const initialPopoverFilters: ScnFilterPopoverValues = useMemo(
    () => ({ status: filters.status }),
    [filters.status]
  );

  const handleApplyFilters = (values: ScnFilterPopoverValues) => {
    setFilters((prev) => ({
      ...prev,
      status: values.status === 'in_use' || values.status === 'inactive' ? values.status : 'all',
    }));
  };

  /** zh-primary spec-name mirror (house locale-ordering rule). */
  const specNameOf = (group: ScnSpecGroup): string | null =>
    (isZh ? (group.specNameZh ?? group.specName) : (group.specName ?? group.specNameZh)) ?? null;

  const columns: TableProps<ScnSpecGroup>['columns'] = [
    {
      title: t('scn:list.columns.scnCode'),
      dataIndex: 'scn',
      key: 'scn',
      width: 260,
      render: (scn: string, record) => {
        const specName = specNameOf(record);
        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5" style={{ alignItems: 'flex-start' }}>
            <PurpleTag style={{ margin: 0 }}>{scn}</PurpleTag>
            {/* Members-only groups (no seeded spec yet) render code only. */}
            {specName && (
              <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                <HighlightText text={specName} searchTerm={filters.search} fallback="" />
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: t('scn:list.columns.memberCount'),
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 140,
      render: (count: number, record) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <span style={{ fontWeight: token.fontWeightStrong }}>{count}</span>
          {/* Board A2 rule: the 0-SKU row reads 未啟用 — count + text, never colour-only. */}
          {record.status === 'inactive' && (
            <Tag
              bordered={false}
              style={{
                color: token.colorTextSecondary,
                backgroundColor: token.colorFillQuaternary,
                borderRadius: radiusTokens.chip,
                margin: 0,
              }}
            >
              {t('scn:list.inactiveBadge')}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: t('scn:list.columns.oemNumbers'),
      dataIndex: 'oemNumbers',
      key: 'oemNumbers',
      width: 240,
      render: (oemNumbers: string[]) => {
        if (!oemNumbers.length) return renderDash(undefined);
        const joined = oemNumbers.join(' · ');
        return (
          <Tooltip title={joined}>
            <span
              style={{
                display: 'inline-block',
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'bottom',
              }}
            >
              {joined}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: t('scn:list.columns.markets'),
      dataIndex: 'markets',
      key: 'markets',
      width: 140,
      render: (markets: string[]) =>
        markets.length ? markets.join(' / ') : renderDash(undefined),
    },
    {
      title: t('scn:list.columns.updated'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (value: string) => formatDate(value),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {t('scn:list.title')}
        </h1>
      </div>

      {/* Search + filter toolbar — no create CTA: the read model has no write
          routes; groups appear by linking products (retroactive PATCH). */}
      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('scn:list.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<ScnFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={handleApplyFilters}
          initialFilters={initialPopoverFilters}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('scn:list.filters.title')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
      </div>

      {/* Table — empty / zero-results / error triad via TableContainer + ListEmptyState */}
      <TableContainer
        errorState={{
          active: listState === 'error',
          title: t('scn:list.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ScnSpecGroup>
          {...tableProps}
          columns={columns}
          rowKey="scn"
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => navigate(`/auth/products/scn/${record.scn}`),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('scn:list.states.noResultsTitle')}
                actionLabel={t('common:actions.clearFilters')}
                onAction={resetFilters}
                actionType="default"
              />
            ) : (
              <ListEmptyState
                title={t('scn:list.states.noGroupsTitle')}
                description={t('scn:list.states.noGroupsDescription')}
              />
            ),
          }}
          pagination={{ ...tableProps.pagination, showSizeChanger: false }}
          scroll={{ x: 1000 }}
        />
      </TableContainer>
    </div>
  );
};

export default ScnSpecListPage;
